import React, { useEffect, useState } from 'react';
import { useSocket } from 'hooks/useSocket';
import { RefreshCw, Maximize2, Minimize2 } from 'lucide-react';

// UTILS
import iconMap from 'utils/iconMap';

// ICONS
const ReseauAIBreakIcon = iconMap['reseauAIBreak'];

const panels = [
  { id: 4, colSpan: 2, rowSpan: 2, colStart: 3, rowStart: 1 },  // Connexions utilisateurs
  { id: 5, colSpan: 2, rowSpan: 3, colStart: 3, rowStart: 3 },  // Utilisation moteurs
  { id: 1, colSpan: 3, rowSpan: 5, colStart: 5, rowStart: 1 },  // Carte
  { id: 3, colSpan: 2, rowSpan: 3, colStart: 1, rowStart: 3 }, // Répartition des clics
  { id: 7, colSpan: 4, rowSpan: 3, colStart: 1, rowStart: 6 }, // Nombre d'utilisateurs par heure
  { id: 2, colSpan: 3, rowSpan: 3, colStart: 5, rowStart: 6 }, // Nouveaux utilisateurs
];

const gridClasses = {
  1: 'col-span-1 md:col-span-1',
  2: 'col-span-1 md:col-span-2',
  3: 'col-span-1 md:col-span-3',
  4: 'col-span-1 md:col-span-4',
  5: 'col-span-1 md:col-span-5',
  6: 'col-span-1 md:col-span-6',
  7: 'col-span-1 md:col-span-7',
};

const rowClasses = {
  1: 'row-span-1',
  2: 'row-span-2',
  3: 'row-span-3',
  4: 'row-span-4',
  5: 'row-span-5',
};

const colStartClasses = {
  1: 'col-start-1 md:col-start-1',
  2: 'col-start-1 md:col-start-2',
  3: 'col-start-1 md:col-start-3',
  4: 'col-start-1 md:col-start-4',
  5: 'col-start-1 md:col-start-5',
};

const rowStartClasses = {
  1: 'row-start-auto md:row-start-1',
  2: 'row-start-auto md:row-start-2',
  3: 'row-start-auto md:row-start-3',
  4: 'row-start-auto md:row-start-4',
  5: 'row-start-auto md:row-start-5',
  6: 'row-start-auto md:row-start-6',
};

const REFRESH_OPTIONS = [
  { label: 'Off', value: 0 },
  { label: 'Auto', value: 10 }, // Par défaut, 10s
  { label: '5s', value: 5 },
  { label: '10s', value: 10 },
  { label: '30s', value: 30 },
  { label: '1m', value: 60 },
  { label: '5m', value: 300 },
  { label: '15m', value: 900 },
  { label: '30m', value: 1800 },
  { label: '1h', value: 3600 },
  { label: '2h', value: 7200 },
  { label: '1d', value: 86400 },
];

const AnalyticsDashboard = () => {
  const [isDark, setIsDark] = useState(false);
  const [isOnInternalNetwork, setIsOnInternalNetwork] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { connectedUsers } = useSocket();
  const [refreshInterval, setRefreshInterval] = useState(() => {
    const saved = localStorage.getItem('dashboard-refresh-interval');
    return saved ? Number(saved) : 0;
  });
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const updateTheme = () => {
      setIsDark(document.documentElement.classList.contains('dark'));
    };

    updateTheme(); // Initial
    const observer = new MutationObserver(updateTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const checkNetworkConnectivity = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_INFRAGATE_URL}/api/check-network`, { method: 'GET' });
        if (response.ok) {
          setIsOnInternalNetwork(true);
        }
      } catch (error) {
        setIsOnInternalNetwork(false);
      }
    };
    checkNetworkConnectivity();
  }, []);

  useEffect(() => {
    localStorage.setItem('dashboard-refresh-interval', refreshInterval);
    if (!refreshInterval) return;
    const interval = setInterval(() => {
      setRefreshKey(k => k + 1);
    }, refreshInterval * 1000);
    return () => clearInterval(interval);
  }, [refreshInterval]);

  const handleManualRefresh = () => setRefreshKey(k => k + 1);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        // Entrer en mode plein écran du navigateur
        await document.documentElement.requestFullscreen();
      } else {
        // Sortir du mode plein écran du navigateur
        await document.exitFullscreen();
      }
    } catch (error) {
      console.error('Erreur lors du changement de mode plein écran:', error);
    }
    
    // Basculer notre mode plein écran personnalisé
    setIsFullscreen(!isFullscreen);
    // Émettre un événement personnalisé pour informer les autres composants
    window.dispatchEvent(new CustomEvent('toggleFullscreen', { detail: !isFullscreen }));
  };

  // Écouter les changements d'état du plein écran du navigateur
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isInFullscreen = document.fullscreenElement !== null;
      setIsFullscreen(isInFullscreen);
      window.dispatchEvent(new CustomEvent('toggleFullscreen', { detail: isInFullscreen }));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  return (
    <div className="h-full">
      <div className="flex items-center gap-2 mb-2">
        <select
          className="h-10 bg-white border border-black dark:border-none dark:bg-dark text-noir dark:text-blanc rounded-lg px-3 text-sm shadow-sm cursor-pointer"
          value={refreshInterval}
          onChange={e => setRefreshInterval(Number(e.target.value))}
        >
          {REFRESH_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <button
          className="h-10 flex items-center px-4 bg-orange hover:bg-framboise text-blanc rounded-lg shadow-md transition-colors ease-in-out duration-300"
          onClick={handleManualRefresh}
          title="Rafraîchir"
        >
          <RefreshCw size={18} className="mr-2" />
          Rafraîchir
        </button>
        <div className="flex-1" />
        <button
          className="h-10 flex items-center px-4 bg-orange hover:bg-framboise text-blanc rounded-lg shadow-md transition-colors ease-in-out duration-300 ml-auto"
          onClick={toggleFullscreen}
          title={isFullscreen ? "Quitter le mode plein écran" : "Mode plein écran"}
        >
          {isFullscreen ? (
            <Minimize2 size={18} className="mr-2" />
          ) : (
            <Maximize2 size={18} className="mr-2" />
          )}
          {isFullscreen ? "Quitter" : "Plein écran"}
        </button>
      </div>
      <div key={refreshKey} className="grid grid-cols-1 md:grid-cols-7 auto-rows-[100px] gap-2">
        {/* Panneau des utilisateurs connectés */}
        <div className="col-span-1 md:col-span-2 row-span-1 md:row-span-2 col-start-1 row-start-auto md:row-start-1 flex flex-col justify-center bg-white dark:bg-[#181B1F] shadow rounded-lg p-2 relative">
          <h6 className="text-sm font-medium text-noir dark:text-[#CCCCDC] mt-4 md:mt-0">Nombre d'utilisateurs en ligne</h6>
          <div className="flex-1 flex items-center justify-center">
            <span className="text-[75px] md:text-[125px] font-medium leading-[1.2] text-center text-[#73BF69]">{connectedUsers}</span>
          </div>
        </div>

        {panels.map(({ id, colSpan, rowSpan, colStart, rowStart }) => (
          <div
            key={id}
            className={`
              ${gridClasses[colSpan]}
              ${rowClasses[rowSpan]}
              ${colStartClasses[colStart]}
              ${rowStartClasses[rowStart]}
              flex items-center justify-center
              bg-white dark:bg-[#181B1F] shadow rounded-lg
            `}
          >
            {isOnInternalNetwork ? (
              <iframe
                src={`https://dsi.altitudeinfra.fr:8081/d-solo/ff37c7bd-cbee-44ce-af16-2b228179db84/statistiques-du-hub?orgId=1&theme=${isDark ? 'dark' : 'light'}&panelId=${id}`}
                className="w-full h-full rounded-lg"
                frameBorder="0"
                title={`Panel ${id}`}
                style={{ minHeight: 0, minWidth: 0 }}
              />
            ) : (
              <div className="flex flex-col items-center justify-center p-4 text-center">
                <ReseauAIBreakIcon className="size-16 mb-4 fill-gray-400" />
                <p className="text-gray-600 dark:text-gray-300">
                  Veuillez vous connecter au VPN pour accéder aux statistiques
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
